import { useState, useCallback, useMemo, useRef } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldAlert, Settings2, ListOrdered, Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { api, type UserChoiceOption, type AppSearchResult } from '../lib/api'
import { queries } from '../lib/queries'
import AppIcon from '../components/AppIcon'
import { useAppSuggest } from '../hooks/useAppSuggest'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { Skeleton } from '../components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'

export const Route = createFileRoute('/admin/rules/')({
  head: () => ({
    meta: [{ title: 'Rules | Admin | Unbreakable' }],
  }),
  component: AdminRulesPage,
})

type PendingConfirm =
  | { type: 'delete-user-choice-option'; id: string; label: string }
  | { type: 'delete-system-rule'; id: string; name: string }
  | null

function AdminRulesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // System rules state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createSlug, setCreateSlug] = useState('')
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createSortOrder, setCreateSortOrder] = useState(0)
  const [createCloneFromId, setCreateCloneFromId] = useState('')

  // User choice options state
  const [showUserChoiceForm, setShowUserChoiceForm] = useState(false)
  const [editingUserChoiceOption, setEditingUserChoiceOption] = useState<UserChoiceOption | null>(null)
  const [ucSlug, setUcSlug] = useState('')
  const [ucLabel, setUcLabel] = useState('')
  const [ucBundleIds, setUcBundleIds] = useState('')
  const [ucDomains, setUcDomains] = useState('')
  const [ucAddAppQuery, setUcAddAppQuery] = useState('')
  const [ucAddSuggestOpen, setUcAddSuggestOpen] = useState(false)
  const ucAddSuggestBlurRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null)

  // Queries
  const { data: protectedAppsData, isLoading: protectedAppsLoading } = useQuery({
    queryKey: queries.admin.protectedApps(),
    queryFn: () => api.getAdminProtectedApps(),
  })
  const items = protectedAppsData?.items ?? []

  const { data: systemRulesData, isLoading: systemRulesLoading } = useQuery({
    queryKey: queries.admin.systemRules(),
    queryFn: () => api.getAdminSystemRules(),
  })
  const systemRules = systemRulesData?.systemRules ?? []

  const { data: userChoiceData, isLoading: userChoiceOptionsLoading } = useQuery({
    queryKey: queries.admin.userChoiceOptions(),
    queryFn: () => api.getUserChoiceOptions(),
  })
  const userChoiceOptions = userChoiceData?.options ?? []

  // App suggest hooks (for user choice options only now — protected apps are read-only)

  const existingUcBundleIds = useMemo(
    () => new Set(ucBundleIds.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)),
    [ucBundleIds]
  )
  const { apps: ucAddSuggestResults, loading: ucAddSuggestLoading } = useAppSuggest(ucAddAppQuery, {
    limit: 15,
    filter: useCallback((app: AppSearchResult) => !app.bundleId || !existingUcBundleIds.has(app.bundleId), [existingUcBundleIds]),
  })

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg)
    setTimeout(() => setSuccessMessage(null), 3000)
  }

  // Mutations
  const createRuleMutation = useMutation({
    mutationFn: (data: { presetSlug: string; name: string; description?: string | null; sortOrder?: number; cloneFromId?: string }) =>
      api.createAdminSystemRule(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: queries.admin.systemRules() })
      setShowCreateForm(false)
      setCreateSlug(''); setCreateName(''); setCreateDescription(''); setCreateSortOrder(0); setCreateCloneFromId('')
      navigate({ to: '/admin/rules/$id', params: { id: created.id } })
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create'),
  })

  const deleteRuleMutation = useMutation({
    mutationFn: (id: string) => api.deleteAdminSystemRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.systemRules() })
      showSuccess('System rule deleted')
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to delete'),
  })

  const saveUserChoiceMutation = useMutation({
    mutationFn: async (params: { editing: UserChoiceOption | null; slug: string; label: string; bundleIds: string[]; domains: string[] }) => {
      if (params.editing) {
        return api.updateUserChoiceOption(params.editing.id, { label: params.label, bundleIds: params.bundleIds, domains: params.domains })
      }
      return api.createUserChoiceOption({ slug: params.slug, label: params.label, bundleIds: params.bundleIds, domains: params.domains })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.userChoiceOptions() })
      closeUserChoiceForm()
      showSuccess(editingUserChoiceOption ? 'User choice option updated' : 'User choice option created')
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to save'),
  })

  const deleteUserChoiceMutation = useMutation({
    mutationFn: (id: string) => api.deleteUserChoiceOption(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.userChoiceOptions() })
      showSuccess('User choice option deleted')
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to delete'),
  })

  // Handlers
  const openUserChoiceForm = (option?: UserChoiceOption) => {
    if (option) { setEditingUserChoiceOption(option); setUcSlug(option.slug); setUcLabel(option.label); setUcBundleIds(option.bundleIds.join('\n')); setUcDomains(option.domains.join('\n')) }
    else { setEditingUserChoiceOption(null); setUcSlug(''); setUcLabel(''); setUcBundleIds(''); setUcDomains('') }
    setShowUserChoiceForm(true)
  }

  const closeUserChoiceForm = () => { setShowUserChoiceForm(false); setEditingUserChoiceOption(null); setUcSlug(''); setUcLabel(''); setUcBundleIds(''); setUcDomains(''); setUcAddAppQuery(''); setUcAddSuggestOpen(false) }

  const handleSaveUserChoiceOption = () => {
    if (!ucSlug.trim() || !ucLabel.trim()) { setError('Slug and label are required'); return }
    const bundleIds = ucBundleIds.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
    const domains = ucDomains.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
    saveUserChoiceMutation.mutate({ editing: editingUserChoiceOption, slug: ucSlug.trim().toLowerCase(), label: ucLabel.trim(), bundleIds, domains })
  }

  const handleConfirmAction = async () => {
    if (!pendingConfirm) return
    if (pendingConfirm.type === 'delete-user-choice-option') { deleteUserChoiceMutation.mutate(pendingConfirm.id) }
    else { deleteRuleMutation.mutate(pendingConfirm.id) }
    setPendingConfirm(null)
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Rule Management</h2>
        <p className="text-gray-500 mt-1">Manage always-available apps, user choice options, and system rules.</p>
      </div>

      {successMessage && <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">{successMessage}</div>}
      {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">{error}</div>}

      {/* Always-Available Apps (read-only — managed via curated catalog import) */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ShieldAlert className="h-5 w-5" />Always-Available Apps</h3>
          <p className="text-sm text-gray-500 mt-1">
            These apps can never be blocked. They are automatically added to every allowlist to ensure the device remains usable.
            Managed via the curated app catalog.
          </p>
        </div>
        <div className="p-6">
          {protectedAppsLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500">No protected apps found. Run the catalog import to populate.</p>
          ) : (
            <ul className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {items.map((item) => (
                <li key={item.bundleId || item.packageName || item.name} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <div className="flex items-center gap-3">
                    <AppIcon icon={item.icon} name={item.name} size={48} boxClassName="w-12 h-12" />
                    <div>
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{item.bundleId || item.packageName}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 uppercase">{item.platform}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* User Choice Options */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Settings2 className="h-5 w-5" />User Choice Options</h3>
            <p className="text-sm text-gray-500 mt-1">Options that families can toggle per device when using a system rule.</p>
          </div>
          <Button onClick={() => openUserChoiceForm()}><Plus className="h-4 w-4 mr-1" />New option</Button>
        </div>
        <div className="p-6 space-y-4">
          {userChoiceOptionsLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : userChoiceOptions.length === 0 ? (
            <p className="text-sm text-gray-500">No user choice options yet.</p>
          ) : (
            <ul className="border border-gray-200 rounded-lg divide-y divide-gray-200">
              {userChoiceOptions.map((opt) => (
                <li key={opt.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="font-medium text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-500 font-mono">{opt.slug}</p>
                    <p className="text-xs text-gray-400 mt-1">{opt.bundleIds.length} app(s) &middot; {opt.domains.length} domain(s)</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => openUserChoiceForm(opt)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setPendingConfirm({ type: 'delete-user-choice-option', id: opt.id, label: opt.label })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* System Rules */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ListOrdered className="h-5 w-5" />System Rules</h3>
            <p className="text-sm text-gray-500 mt-1">These rules appear for all families in the Rules list.</p>
          </div>
          <Button onClick={() => setShowCreateForm(true)}><Plus className="h-4 w-4 mr-1" />New system rule</Button>
        </div>
        <div className="p-6 space-y-4">
          {systemRulesLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : systemRules.length === 0 ? (
            <p className="text-sm text-gray-500">No system rules yet.</p>
          ) : (
            <ul className="border border-gray-200 rounded-lg divide-y divide-gray-200">
              {systemRules.map((rule) => (
                <li key={rule.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="font-medium text-gray-900">{rule.name}</p>
                    <p className="text-xs text-gray-500 font-mono">{rule.presetSlug ?? '—'}</p>
                    {rule.description && <p className="text-sm text-gray-600 mt-0.5">{rule.description}</p>}
                    <p className="text-xs text-gray-400 mt-1">Order: {rule.sortOrder ?? '—'} &middot; Assigned to {rule.assignmentCount} device(s)</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to="/admin/rules/$id" params={{ id: rule.id }}>
                      <Button variant="outline" size="sm">Edit full rule</Button>
                    </Link>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" disabled={rule.assignmentCount > 0} onClick={() => setPendingConfirm({ type: 'delete-system-rule', id: rule.id, name: rule.name })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* User Choice Option Form Dialog */}
      <Dialog open={showUserChoiceForm} onOpenChange={(open) => { if (!open) closeUserChoiceForm() }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingUserChoiceOption ? 'Edit User Choice Option' : 'New User Choice Option'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Slug (unique identifier)</Label>
              <Input value={ucSlug} onChange={(e) => setUcSlug(e.target.value)} className="font-mono" placeholder="spotify" disabled={!!editingUserChoiceOption} />
              {editingUserChoiceOption && <p className="text-xs text-gray-400 mt-1">Slug cannot be changed after creation.</p>}
            </div>
            <div>
              <Label>Label (shown to families)</Label>
              <Input value={ucLabel} onChange={(e) => setUcLabel(e.target.value)} placeholder="Spotify" />
            </div>
            <div>
              <Label>Bundle IDs (one per line)</Label>
              <p className="text-xs text-gray-500 mb-2">Search to add an app, or paste bundle IDs below.</p>
              <div className="relative mb-2">
                <Input
                  value={ucAddAppQuery}
                  onChange={(e) => setUcAddAppQuery(e.target.value)}
                  onFocus={() => { if (ucAddSuggestBlurRef.current) { clearTimeout(ucAddSuggestBlurRef.current); ucAddSuggestBlurRef.current = null }; setUcAddSuggestOpen(true) }}
                  onBlur={() => { ucAddSuggestBlurRef.current = setTimeout(() => setUcAddSuggestOpen(false), 200) }}
                  placeholder="Search app (e.g. Spotify, YouTube)"
                  className="font-mono text-sm"
                />
                {ucAddSuggestOpen && ucAddAppQuery.trim().length >= 2 && (
                  <div className="absolute top-full left-0 z-50 mt-1 w-full border border-gray-200 rounded-lg bg-white shadow-lg max-h-60 overflow-y-auto">
                    {ucAddSuggestLoading ? (
                      <div className="p-3 text-sm text-gray-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Searching...</div>
                    ) : ucAddSuggestResults.length > 0 ? (
                      <ul className="py-1">
                        {ucAddSuggestResults.filter(app => app.bundleId).map((app) => (
                          <li key={app.bundleId}>
                            <button type="button" onClick={() => { setUcBundleIds((prev) => prev.trim() ? prev.trimEnd() + '\n' + app.bundleId! : app.bundleId!); setUcAddAppQuery(''); setUcAddSuggestOpen(false) }} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-100 transition-colors">
                              <AppIcon icon={app.icon} name={app.name} size={48} />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 truncate">{app.name}</p>
                                <p className="text-xs text-gray-500 font-mono truncate">{app.bundleId}</p>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="p-3 text-sm text-gray-500">No apps found.</p>
                    )}
                  </div>
                )}
              </div>
              <textarea value={ucBundleIds} onChange={(e) => setUcBundleIds(e.target.value)} placeholder="com.spotify.client" className="w-full h-24 rounded-md border border-gray-300 px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <Label>Domains (one per line)</Label>
              <textarea value={ucDomains} onChange={(e) => setUcDomains(e.target.value)} placeholder={"spotify.com\n*.spotify.com"} className="w-full h-24 rounded-md border border-gray-300 px-3 py-2 text-sm font-mono" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeUserChoiceForm}>Cancel</Button>
              <Button disabled={saveUserChoiceMutation.isPending || !ucSlug.trim() || !ucLabel.trim()} onClick={handleSaveUserChoiceOption}>
                {saveUserChoiceMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {editingUserChoiceOption ? 'Save Changes' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create System Rule Dialog */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create system rule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Preset slug (unique)</Label><Input value={createSlug} onChange={(e) => setCreateSlug(e.target.value)} className="font-mono" placeholder="my-preset" /></div>
            <div><Label>Name</Label><Input value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="My Preset" /></div>
            <div><Label>Description (optional)</Label><Input value={createDescription} onChange={(e) => setCreateDescription(e.target.value)} placeholder="Short description" /></div>
            <div><Label>Sort order</Label><Input type="number" value={createSortOrder} onChange={(e) => setCreateSortOrder(parseInt(e.target.value, 10) || 0)} /></div>
            <div>
              <Label>Clone from (optional)</Label>
              <Select value={createCloneFromId} onValueChange={(v) => setCreateCloneFromId(v ?? '')}>
                <SelectTrigger><SelectValue placeholder="-- Empty rule --" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- Empty rule --</SelectItem>
                  {systemRules.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button disabled={createRuleMutation.isPending || !createSlug.trim() || !createName.trim()} onClick={() => createRuleMutation.mutate({ presetSlug: createSlug.trim(), name: createName.trim(), description: createDescription.trim() || null, sortOrder: createSortOrder, cloneFromId: createCloneFromId && createCloneFromId !== '_none' ? createCloneFromId : undefined })}>
                {createRuleMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <AlertDialog open={pendingConfirm !== null} onOpenChange={(open) => { if (!open) setPendingConfirm(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingConfirm?.type === 'delete-user-choice-option' ? 'Delete User Choice Option' : 'Delete System Rule'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingConfirm?.type === 'delete-user-choice-option'
                ? `Delete "${pendingConfirm.label}"? This cannot be undone.`
                : pendingConfirm?.type === 'delete-system-rule'
                  ? `Delete "${pendingConfirm.name}"? This cannot be undone.`
                  : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { void handleConfirmAction() }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
